import React, { ReactNode, Fragment, useState, useEffect } from 'react';
import { Text, StyleSheet, Animated, TouchableOpacity } from 'react-native';
import { EventEmitter } from 'fbemitter';

import { TOAST } from '../constants/colors';

type ToastAction = {
  label: string;
  onPress: () => void;
};

type ToastConfig = {
  message: string;
  duration?: number;
  action?: ToastAction;
  onToastEnd?: () => void;
};

type Props = {
  children: ReactNode;
};

let toastEmitter = new EventEmitter();
export let showToast = ({
  message,
  duration,
  action,
  onToastEnd,
}: ToastConfig) => {
  toastEmitter.emit('showToast', { message, duration, action, onToastEnd });
};

const emptyFunction = () => {};

function ToastProvider(props: Props) {
  const initialState = {
    isShowing: false,
    isVisible: false,
    message: '',
    duration: 2000,
    action: {
      label: '',
      onPress: emptyFunction,
    },
    onToastEnd: emptyFunction,
  };

  let [state, setState] = useState(initialState);

  let { isShowing, isVisible, message, duration, action, onToastEnd } = state;

  let timeoutID: ReturnType<typeof setTimeout>;
  let animatedTranslateValue = new Animated.Value(0);
  let animatedFadeValue = new Animated.Value(0);

  useEffect(() => {
    toastEmitter.addListener('showToast', (toastConfig: ToastConfig) => {
      isShowing ||
        setState({
          ...state,
          isShowing: true,
          isVisible: true,
          message: toastConfig.message,
          duration: toastConfig.duration || duration,
          action: toastConfig.action || action,
          onToastEnd: toastConfig.onToastEnd || onToastEnd,
        });
    });

    return () => {
      toastEmitter.removeAllListeners();
    };
  }, [isShowing]);

  let renderToast = () => {
    Animated.parallel([
      Animated.timing(animatedTranslateValue, {
        toValue: -24,
        duration: 150,
      }),
      Animated.timing(animatedFadeValue, {
        toValue: 1,
        duration: 150,
      }),
    ]).start(() => {
      timeoutID = setTimeout(() => {
        closeToast();
      }, duration);
    });

    const animatedTranslateStyle = {
      transform: [{ translateY: animatedTranslateValue }],
    };

    return (
      <Animated.View
        style={[
          styles.snackbarContainer,
          animatedTranslateStyle,
          { opacity: animatedFadeValue },
        ]}
      >
        <Text style={styles.snackbarText} numberOfLines={1}>
          {message}
        </Text>

        <TouchableOpacity onPress={action.onPress}>
          <Text style={styles.touchableText}>{action.label}</Text>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  let closeToast = () => {
    Animated.timing(animatedFadeValue, {
      toValue: 0,
      duration: 200,
    }).start(() => {
      setState({
        ...initialState,
      });
      onToastEnd();
      clearTimeout(timeoutID);
    });
  };

  return (
    <Fragment>
      {props.children}
      {isVisible && renderToast()}
    </Fragment>
  );
}

const styles = StyleSheet.create({
  snackbarContainer: {
    flexDirection: 'row',
    position: 'absolute',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'space-between',
    bottom: 0,
    width: '88.88%',
    backgroundColor: TOAST.background,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 4,
  },
  snackbarText: {
    color: TOAST.labelText,
    fontSize: 16,
  },
  touchableText: {
    color: TOAST.actionText,
    fontSize: 16,
  },
});

export default ToastProvider;
